import { c as _c } from "react/compiler-runtime";
import { Button, Menu, Popover, Select, Switch, Tooltip } from '@mantine/core';
import { useDbForm } from 'components/hooks/useDbForm';
import { doSafe, err, ok } from 'shared/promise';
import { fmtDate } from 'shared/format';
import { NotNull } from 'shared/type';
import { ChildrenProps } from 'shared/ui/uiType';
import { isNullOrEmpty, groupMap, notNullOrEmpty, mapEntries } from 'shared/collection';
import { LabelEmptyList, Markdown, LabelForm, WrapIf } from 'shared/ui';
import { OverflowMenu } from 'components/Menu';
import { HiTrash } from 'react-icons/hi2';
import { openConfirmModal } from '@mantine/modals';
import { showError } from 'components/notify';
import { usePortal } from 'components/portal/portal';
import { ProfileUI, zProfileAdminRow } from '../profileTypes';
import { SAvatar } from '../SAvatar';
import { useQuerySbSync } from 'utils/db/useQuerySb';
import { AdminSection, EditLimit, editLimitProps, RoleInput, useAdminSection } from './AdminComponents';
import { DateTime as D } from 'luxon';
import { firstBy, indexBy, keys, partition, pick, sortBy, values } from 'remeda';
import { match, P } from 'ts-pattern';
import { SkeletonList } from 'components/Skeletons';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { avatarNames } from 'components/portal/profile';
import { InfoLabel } from 'components/Info';
import { sb } from 'utils/db';
import { Workspace as W } from 'components/portal/workspace';
import { AdminInviteLinks, AdminInviteDomains, AdminEmailInvites } from './AdminInvite';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import { licenseApi } from 'shared/forecast/license';
import { useDebouncedFn } from 'components/hooks';
import { StringNumberInput } from 'components/form/StringNumberInput';
type AdminUsersSectionMd = {
  title: string;
  menuTitle?: string;
};
const adminUsersSectionMd = {
  inviteLinks: {
    title: 'Participant invite links',
    menuTitle: 'Share a link for participants to join'
  },
  inviteDomains: {
    title: 'Open sign-up for domains'
  },
  emailInvites: {
    title: 'Email invites',
    menuTitle: 'Invite a user by email'
  },
  users: {
    title: 'Users',
    menuTitle: 'Manage users'
  },
  requests: {
    title: 'Access requests',
    menuTitle: 'Respond to access requests'
  }
} satisfies Record<string, AdminUsersSectionMd>;
export type AdminUsersSections = keyof typeof adminUsersSectionMd;
export const getAdminUserSectionMd = (name: AdminUsersSections) => ({
  ...adminUsersSectionMd[name],
  name
});
export const adminUserSections = keys(adminUsersSectionMd).map(name => ({
  name,
  ...adminUsersSectionMd[name]
}) as AdminUsersSectionMd & {
  name: AdminUsersSections;
});
const defaultProfileNames = new Set(values(avatarNames).flat().map(n => n.toLowerCase()));
export const AdminUsers = t0 => {
  const $ = _c(19);
  const {
    uWorkspace
  } = t0;
  const uUsers = useAdminUsersList();
  let t1;
  if ($[0] !== uWorkspace.id) {
    t1 = {
      queryKey: ["workspace_global_admin", uWorkspace.id],
      queryFn: async () => await sb.from("workspace_global_admin").select("cfg").eq("id", uWorkspace.id).maybeSingle().then(_temp)
    };
    $[0] = uWorkspace.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useQuery(t1);
  let t2;
  if ($[2] !== uUsers || $[3] !== uWorkspace) {
    t2 = <AdminInviteLinks uUsers={uUsers} uWorkspace={uWorkspace} />;
    $[2] = uUsers;
    $[3] = uWorkspace;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== uWorkspace) {
    t3 = <AdminInviteDomains uWorkspace={uWorkspace} />;
    $[5] = uWorkspace;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== uUsers) {
    t4 = <AdminEmailInvites uUsers={uUsers} />;
    $[7] = uUsers;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  if ($[9] !== uUsers || $[10] !== uWorkspace) {
    t5 = <AdminRequests uUsers={uUsers} uWorkspace={uWorkspace} />;
    t6 = <AdminUsersList uUsers={uUsers} uWorkspace={uWorkspace} />;
    $[9] = uUsers;
    $[10] = uWorkspace;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="space-y-2 max-w-[80em] m-2">{t2}{t3}{t4}{t5}{t6}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
export const useAdminUsersList = () => {
  const $ = _c(47);
  const {
    profiles,
    workspace_id,
    profile
  } = usePortal();
  let t0;
  if ($[0] !== workspace_id) {
    t0 = b => workspace_id == "global" ? b.select("*").neq("cfg->roles", "[]") : b.select("*");
    $[0] = workspace_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== workspace_id) {
    t1 = {
      workspace_id
    };
    $[2] = workspace_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!workspace_id;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      enabled: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: usersRaw,
    update,
    isPending,
    ...res
  } = useQuerySbSync({
    name: "admin-users",
    schema: zProfileAdminRow,
    table: "profile",
    select: t0,
    selectFrom: "profile_user_admin",
    filter: t1,
    options: t3,
    onDbChange: c => {
      if (c.eventType == "INSERT") {
        usersRefetchDebounced();
      }
      usageRefetchDebounced();
    },
    clientFilter: _temp2
  });
  let t4;
  if ($[6] !== workspace_id) {
    t4 = licenseApi.queryProps("getUsage", {
      workspace_id
    }, {
      onError: _temp3
    });
    $[6] = workspace_id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const usageQuery = useQuery(t4);
  const usageRefetchDebounced = useDebouncedFn(usageQuery.refetch, 2000);
  const usersRefetchDebounced = useDebouncedFn(res.refetch, 2000);
  let t5;
  if ($[8] !== update || $[9] !== workspace_id) {
    t5 = {
      mutationFn: async t6 => {
        const {
          id,
          email
        } = t6;
        if (!workspace_id) {
          throw new Error("can't remove user, need a workspace_id");
        }
        const removeProfile = async () => {
          const {
            error
          } = await update({
            id,
            deleted: true
          });
          if (error) {
            showError("error removing profile", error.message);
          }
        };
        const removeInvite = async () => {
          if (!email) {
            return;
          }
          const {
            error: errorInvite
          } = await sb.from("invite").insert({
            workspace_id,
            roles: [],
            email,
            status_msg: "removed user",
            status: "removed"
          });
          if (errorInvite) {
            showError("error removing invite", errorInvite.message);
          }
        };
        await Promise.allSettled([removeProfile(), removeInvite()]);
      }
    };
    $[8] = update;
    $[9] = workspace_id;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const removeUser = useMutation(t5);
  let t6;
  if ($[11] !== workspace_id) {
    t6 = {
      queryFn: async () => await sb.from("activity_latest").select("profile_id, at").match({
        workspace_id
      }).then(_temp4),
      queryKey: ["activity_latest", workspace_id],
      refetchInterval: 60000
    };
    $[11] = workspace_id;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data: lastSeenData
  } = useQuery(t6);
  let t7;
  bb0: {
    const profileByWsId = profiles?.normals ? indexBy(profiles.normals, _temp5) : undefined;
    if (!usersRaw || !profileByWsId) {
      let t8;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          users: undefined,
          otherWorkspaceUsers: undefined
        };
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    let users;
    if ($[14] !== lastSeenData || $[15] !== usersRaw) {
      const seenById = indexBy(lastSeenData?.filter(_temp6) ?? [], _temp7);
      const usersAndParticipants = usersRaw?.map(u_0 => ({
        ...u_0,
        seen: seenById[u_0.id]?.at
      }));
      const [participantsRaw, t9] = partition(usersAndParticipants, _temp8);
      users = t9;
      t8 = sortBy(participantsRaw, [_temp9, "asc"], [_temp10, "asc"]);
      $[14] = lastSeenData;
      $[15] = usersRaw;
      $[16] = t8;
      $[17] = users;
    } else {
      t8 = $[16];
      users = $[17];
    }
    const participants = t8;
    let t9;
    if ($[18] !== participants || $[19] !== users) {
      t9 = {
        users,
        participants
      };
      $[18] = participants;
      $[19] = users;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t7 = t9;
  }
  const {
    users: users_0,
    participants: participants_0
  } = t7;
  let t8;
  if ($[21] !== profile.license || $[22] !== profile.workspace || $[23] !== usageQuery.data?.last_billing_date || $[24] !== usageQuery.data?.participants_since_last_billing || $[25] !== usageQuery.data?.users_now) {
    t8 = type => {
      const license = profile.license;
      const limitCfg = profile.workspace[match(type).with("users", _temp11).with("participants", _temp12).exhaustive()] ?? (license?.plan == "trial" ? {
        type: "plan"
      } : {
        type: "no-limit"
      });
      const limit = match(limitCfg).with({
        type: "custom"
      }, _temp13).with({
        type: "plan"
      }, _ => license?.[type] ?? 0).with({
        type: "no-limit"
      }, _temp14).exhaustive();
      const value_0 = match(type).with("users", () => usageQuery.data?.users_now).with("participants", () => usageQuery.data?.participants_since_last_billing).exhaustive();
      const exceeding = value_0 != null && limit != null && value_0 >= limit;
      const additional = value_0 != null && license ? value_0 - (license?.[type] ?? 0) : undefined;
      return {
        type,
        limitCfg,
        limit,
        value: value_0,
        exceeding,
        limitReadonly: license?.plan == "trial",
        lastBillingDate: doSafe(usageQuery.data?.last_billing_date, _temp15),
        additional
      };
    };
    $[21] = profile.license;
    $[22] = profile.workspace;
    $[23] = usageQuery.data?.last_billing_date;
    $[24] = usageQuery.data?.participants_since_last_billing;
    $[25] = usageQuery.data?.users_now;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const limit_0 = t8;
  let t9;
  if ($[27] !== limit_0) {
    t9 = {
      usersLimit: limit_0("users"),
      participantsLimit: limit_0("participants")
    };
    $[27] = limit_0;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const res_0 = t9;
  const usage = res_0;
  let t10;
  if ($[29] !== users_0) {
    t10 = new Set(users_0?.map(_temp16).filter(notNullOrEmpty));
    $[29] = users_0;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const userEmails = t10;
  let t11;
  if ($[31] !== userEmails) {
    t11 = email_0 => sb.rpc("email_completions", {
      value: email_0
    }).then(r_0 => r_0.error ? err(r_0.error.message) : ok({
      data: r_0.data.filter(u_3 => !userEmails.has(u_3.email))
    }));
    $[31] = userEmails;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const emailCompletions = t11;
  const t12 = removeUser.mutateAsync;
  const t13 = isPending || removeUser.isPending || usageQuery.isPending;
  let t14;
  if ($[33] !== usageQuery) {
    t14 = pick(usageQuery, ["isPending", "refetch"]);
    $[33] = usageQuery;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== emailCompletions || $[36] !== participants_0 || $[37] !== profile.license || $[38] !== removeUser.mutateAsync || $[39] !== res || $[40] !== t13 || $[41] !== t14 || $[42] !== update || $[43] !== usage || $[44] !== users_0 || $[45] !== workspace_id) {
    t15 = {
      workspace_id,
      users: users_0,
      participants: participants_0,
      update,
      removeUser: t12,
      isPending: t13,
      usage,
      usageQuery: t14,
      license: profile.license,
      emailCompletions,
      ...res
    };
    $[35] = emailCompletions;
    $[36] = participants_0;
    $[37] = profile.license;
    $[38] = removeUser.mutateAsync;
    $[39] = res;
    $[40] = t13;
    $[41] = t14;
    $[42] = update;
    $[43] = usage;
    $[44] = users_0;
    $[45] = workspace_id;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
};
export type UseAdminUsersList = ReturnType<typeof useAdminUsersList>;
export type AdminUsersLimit = NonNullable<UseAdminUsersList['usage']>[keyof UseAdminUsersList['usage']];
export const useAdminUsersSection = name => {
  const $ = _c(8);
  const t0 = name != "users";
  let t1;
  if ($[0] !== name || $[1] !== t0) {
    t1 = {
      name,
      startCollapsed: t0
    };
    $[0] = name;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const adminSectionState = useAdminSection(t1);
  let t2;
  if ($[3] !== name) {
    t2 = getAdminUserSectionMd(name);
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== adminSectionState || $[6] !== t2) {
    t3 = {
      ...adminSectionState,
      ...t2
    };
    $[5] = adminSectionState;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const AdminUsersList = t0 => {
  const $ = _c(25);
  const {
    uUsers,
    uWorkspace
  } = t0;
  const {
    users: usersStored,
    workspace_id,
    isLoading,
    usage,
    license
  } = uUsers;
  const users = usersStored ? sortBy(usersStored, [_temp18, "desc"]) : undefined;
  const adminSectionState = useAdminUsersSection("users");
  if (!workspace_id || !uWorkspace.form.values) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const T0 = AdminSection;
  const t1 = true;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-[12em]">Users</div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== license) {
    t3 = u_0 => <><b className="pl-4">{u_0.value}</b><span className="opacity-60 text-sm"> users</span>{license && license.users && <><span className="opacity-30 px-2 text-sm"> / </span><b className="opacity-60">{license.users}</b><span className="opacity-60 text-sm"> in plan </span>{u_0.exceeding && <><b className="pl-2">+{u_0.additional}</b><span className="opacity-60 text-sm"> additional</span></>}</>}</>;
    $[2] = license;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== usage.usersLimit) {
    t4 = doSafe(usage.usersLimit, t3);
    $[4] = t3;
    $[5] = usage.usersLimit;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="flex items-center gap-2">{t2}<div className="text-[--mantine-color-text] ">{t4}</div></div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = uUsers.isPending || uWorkspace.form.isDirty;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <InfoLabel>A list of people who have access to this workspace. You can change their roles or remove them.</InfoLabel>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = uWorkspace.form as any;
  let t9;
  if ($[10] !== t8 || $[11] !== uUsers.usage.usersLimit) {
    t9 = editLimitProps({
      limit: uUsers.usage.usersLimit,
      workspaceForm: t8
    });
    $[10] = t8;
    $[11] = uUsers.usage.usersLimit;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <EditLimit {...t9} />;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== uUsers) {
    t11 = t12 => {
      const {
        users: users_0
      } = t12;
      return users_0.length == 0 ? <LabelEmptyList>No users yet..</LabelEmptyList> : <table className="table-auto border-separate border-spacing-y-6 border-spacing-x-2"><tbody>{users_0.map(u_1 => <UserTr key={u_1.id} {...{
            u: u_1,
            uUsers
          }} />)}</tbody></table>;
    };
    $[15] = uUsers;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = match({
    isLoading,
    users
  }).with({
    isLoading: true
  }, _temp19).with({
    users: P.not(P.nullish)
  }, t11).otherwise(_temp20);
  let t13;
  if ($[17] !== T0 || $[18] !== adminSectionState || $[19] !== t10 || $[20] !== t12 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t13 = <T0 {...adminSectionState} sticky={t1} title={t5} isDirty={t6}>{t7}{t10}{t12}</T0>;
    $[17] = T0;
    $[18] = adminSectionState;
    $[19] = t10;
    $[20] = t12;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};
type AdminUserItem = NotNull<UseAdminUsersList['users']>[number];
const UserTr = t0 => {
  const $ = _c(32);
  const {
    u,
    uUsers: t1
  } = t0;
  const {
    updateCfg,
    removeUser
  } = t1;
  const {
    id,
    email,
    seen
  } = u;
  let t2;
  if ($[0] !== u.cfg) {
    t2 = {
      stored: u.cfg
    };
    $[0] = u.cfg;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const form = useDbForm(t2);
  if (!form.values) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <></>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  const {
    roles
  } = form.values;
  const {
    avatar_url,
    name
  } = u.cfg;
  let t3;
  if ($[3] !== avatar_url || $[4] !== email || $[5] !== id || $[6] !== name) {
    t3 = {
      name,
      avatar_url,
      email,
      id
    };
    $[3] = avatar_url;
    $[4] = email;
    $[5] = id;
    $[6] = name;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== seen) {
    t4 = seen && <div className="opacity-50 text-sm">seen {fmtDate(seen, {
        showTz: true
      })}</div>;
    $[8] = seen;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <td className="min-w-[20em]"><ProfileCell {...t3}>{t4}</ProfileCell></td>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      input: "min-w-[25em]"
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== form || $[15] !== id || $[16] !== updateCfg) {
    t7 = async roles_0 => {
      form.setValues({
        roles: roles_0
      });
      const {
        error
      } = await updateCfg({
        id,
        cfg_updates: {
          roles: roles_0
        }
      });
      if (error) {
        showError("error updating roles", error.toString());
      }
    };
    $[14] = form;
    $[15] = id;
    $[16] = updateCfg;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== roles || $[19] !== t7) {
    t8 = <td><RoleInput label={undefined} value={roles} classNames={t6} onChange={t7} /></td>;
    $[18] = roles;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <HiTrash />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== email || $[23] !== id || $[24] !== name || $[25] !== removeUser) {
    t10 = <td><OverflowMenu><Menu.Item leftSection={t9} onClick={() => {
          openConfirmModal({
            title: "Remove user?",
            centered: true,
            children: <p>Are you sure you want to remove user:{" "}<b>{name}{email ? ` (${email})` : ""}</b></p>,
            labels: {
              confirm: "Remove user",
              cancel: "Cancel"
            },
            confirmProps: {
              color: "red",
              variant: "filled"
            },
            onConfirm: async () => {
              removeUser({
                id,
                email
              });
            }
          });
        }}>Remove user</Menu.Item></OverflowMenu></td>;
    $[22] = email;
    $[23] = id;
    $[24] = name;
    $[25] = removeUser;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== id || $[28] !== t10 || $[29] !== t5 || $[30] !== t8) {
    t11 = <tr key={id}>{t5}{t8}{t10}</tr>;
    $[27] = id;
    $[28] = t10;
    $[29] = t5;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};
export const ProfileCell = t0 => {
  const $ = _c(14);
  const {
    email,
    avatar_url,
    name,
    children
  } = t0;
  let t1;
  if ($[0] !== avatar_url || $[1] !== name) {
    t1 = avatar_url || name ? <SAvatar profile={{
      name,
      avatar_url
    }} className="mt-1" /> : null;
    $[0] = avatar_url;
    $[1] = name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== name) {
    t2 = name && <b className="sensitive">{name}</b>;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== email) {
    t3 = <p className="sensitive">{email}</p>;
    $[5] = email;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t2 || $[9] !== t3) {
    t4 = <div>{t2}{t3}{children}</div>;
    $[7] = children;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t4) {
    t5 = <div className="flex gap-4">{t1}{t4}</div>;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
const requestMd = `Requests from people who open a link within a workspace (e.g. a question) and they don't have permission to it. This can be a user who hasn't used Swift before, or something with access to another workspace but not this one.
\nThe access request will appear in this list, and the workspace owner will receive a notification.
\nNote: the workspace owner receives notifications for these
`;
export const AdminRequests = t0 => {
  const $ = _c(12);
  const {
    uUsers,
    uWorkspace
  } = t0;
  const {
    users,
    isPending
  } = uUsers;
  const adminSectionState = useAdminUsersSection("requests");
  if (!users) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== adminSectionState || $[2] !== isPending || $[3] !== uUsers || $[4] !== uWorkspace || $[5] !== users) {
    const requests = uWorkspace.requests?.filter(r => r.status == "requested" && !users.some(u => u.email == r.email && !u.deleted && notNullOrEmpty(u.cfg.roles)));
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        container: "space-y-4"
      };
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <InfoLabel><div><Markdown>{requestMd}</Markdown></div></InfoLabel>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== uUsers || $[10] !== uWorkspace) {
      t4 = rs => rs.map(r_0 => <div key={r_0.key} className="grid grid-cols-3 gap-4 w-full"><RequestTr key={r_0.key} {...{
          r: r_0,
          uUsers,
          uWorkspace
        }} /></div>);
      $[9] = uUsers;
      $[10] = uWorkspace;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t1 = <AdminSection {...adminSectionState} isDirty={isPending} num={requests?.length} classNames={t2} startCollapsed={!requests?.length}>{t3}{match(requests).with([], _temp21).with(P.not(P.nullish), t4).otherwise(_temp22)}</AdminSection>;
    $[1] = adminSectionState;
    $[2] = isPending;
    $[3] = uUsers;
    $[4] = uWorkspace;
    $[5] = users;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const RequestTr = t0 => {
  const $ = _c(50);
  const {
    r,
    uUsers: t1,
    uWorkspace: t2
  } = t0;
  const {
    isPending,
    refetch,
    usage
  } = t1;
  const {
    updateRequest,
    id: workspace_id
  } = t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      initialValues: {
        roles: ["forecaster"]
      }
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const addForm = useForm(t3);
  const [addOpen, setAddOpen] = useState(false);
  const {
    key,
    email,
    msg,
    requestAt
  } = r;
  let t4;
  if ($[1] !== addForm.values || $[2] !== email || $[3] !== key || $[4] !== refetch || $[5] !== updateRequest || $[6] !== workspace_id) {
    t4 = {
      mutationFn: async () => {
        const user_id = await sb.rpc("user_id_from_email", {
          p_workspace_id: workspace_id,
          p_email: email
        }).then(r_0 => {
          if (r_0.error) {
            throw r_0.error;
          }
          if (isNullOrEmpty(r_0.data)) {
            throw new Error(`can't find user_id for email ${email}`);
          }
          return r_0.data;
        });
        await sb.rpc("profile_upsert", {
          p_user_id: user_id,
          p_workspace_id: workspace_id,
          p_cfg: {
            roles: addForm.values.roles
          }
        }).throwOnError();
        await updateRequest(key, {
          status: "add"
        });
        await refetch();
      }
    };
    $[1] = addForm.values;
    $[2] = email;
    $[3] = key;
    $[4] = refetch;
    $[5] = updateRequest;
    $[6] = workspace_id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const addUserMutation = useMutation(t4);
  let t5;
  if ($[8] !== email) {
    t5 = <div className="font-semibold">{email}</div>;
    $[8] = email;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== requestAt) {
    t6 = fmtDate(requestAt, {
      showTime: true,
      showTz: true
    });
    $[10] = requestAt;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="opacity-60">{t6}</div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LabelForm>message</LabelForm>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== msg) {
    t10 = <div className="flex-grow">{t9}{isNullOrEmpty(msg) ? <div className="opacity-30">no message given...</div> : <Markdown className="bg-gray-50 rounded-md p-2 flex-grow">{msg}</Markdown>}</div>;
    $[18] = msg;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== key || $[21] !== updateRequest) {
    t11 = <Button variant="filled" onClick={async () => {
      await updateRequest(key, {
        status: "ignore"
      });
    }}>Ignore</Button>;
    $[20] = key;
    $[21] = updateRequest;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Popover.Target><Button variant="filled" onClick={() => setAddOpen(true)}>Add...</Button></Popover.Target>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== addForm) {
    t13 = addForm.getInputProps("roles");
    $[24] = addForm;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <RoleInput {...t13} withinPortal={false} />;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isPending || addUserMutation.isPending;
  let t16;
  if ($[28] !== addUserMutation) {
    t16 = () => addUserMutation.mutate(undefined, {
      onSuccess: () => setAddOpen(false),
      onError: _temp24
    });
    $[28] = addUserMutation;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15 || $[31] !== t16 || $[32] !== usage.usersLimit.exceeding) {
    t17 = <Button variant="filled" loading={t15} disabled={usage.usersLimit.exceeding} onClick={t16}>Add user</Button>;
    $[30] = t15;
    $[31] = t16;
    $[32] = usage.usersLimit.exceeding;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17 || $[35] !== usage.usersLimit.exceeding) {
    t18 = <WrapIf if={usage.usersLimit.exceeding} wrap={_temp23}>{t17}</WrapIf>;
    $[34] = t17;
    $[35] = usage.usersLimit.exceeding;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t14 || $[38] !== t18) {
    t19 = <Popover.Dropdown className="flex gap-4 items-end">{t14}{t18}</Popover.Dropdown>;
    $[37] = t14;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== addOpen || $[41] !== t19) {
    t20 = <Popover opened={addOpen} onChange={setAddOpen}>{t12}{t19}</Popover>;
    $[40] = addOpen;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t11 || $[44] !== t20) {
    t21 = <div className="flex items-center justify-end gap-2">{t11}{t20}</div>;
    $[43] = t11;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t10 || $[47] !== t21 || $[48] !== t8) {
    t22 = <>{t8}{t10}{t21}</>;
    $[46] = t10;
    $[47] = t21;
    $[48] = t8;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  return t22;
};
function _temp(r) {
  if (r.error) {
    showError("error getting workspace usage info", r.error.message);
    return null;
  }
  return WT.zGlobalAdmin.optional().parse(r.data.cfg);
}
function _temp2(u) {
  return u.is_anonymous ? true : !u.deleted;
}
function _temp3(e) {
  return showError(e.message);
}
function _temp4(r) {
  if (r.error) {
    throw r.error;
  }
  return r.data;
}
function _temp5(p) {
  return p.workspace_id;
}
function _temp6(s) {
  return s.profile_id;
}
function _temp7(s_0) {
  return s_0.profile_id;
}
function _temp8(u_1) {
  return u_1.is_anonymous ?? false;
}
function _temp9(p_0) {
  return p_0.deleted ? 1 : 0;
}
function _temp10(p_1) {
  return p_1.created_at;
}
function _temp11() {
  return "limitUsers" as const;
}
function _temp12() {
  return "limitParticipants" as const;
}
function _temp13(t0) {
  const {
    value
  } = t0;
  return value;
}
function _temp14() {}
function _temp15(d) {
  return D.fromISO(d);
}
function _temp16(u_2) {
  return u_2.email;
}
function _temp17(d) {
  return D.fromISO(d).toMillis();
}
function _temp18(u) {
  return doSafe(u.seen, _temp17) ?? 0;
}
function _temp19() {
  return <SkeletonList className="max-w-[20em]" />;
}
function _temp20() {
  return <></>;
}
function _temp21() {
  return <LabelEmptyList>No pending requests yet...</LabelEmptyList>;
}
function _temp22() {
  return null;
}
function _temp23(c) {
  return <Tooltip label="Invites disabled because the user limit has been reached">{c}</Tooltip>;
}
function _temp24(e) {
  return showError(e);
}